import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';
import {
    getAllModifierProductsForRootReferenceId,
    IAdjustmentReadResourceV22,
    IBasketAdjustment,
    IBasketItem,
    IItemReadResourceV10
} from 'components/basket/model/Basket';
import { IEnrichedProduct, IProduct } from 'components/menu/model/Menu';
import { Timeslot } from 'components/timeslots/model/Timeslot';
import { isDefined, isNumber } from 'lib/typeInference';
import { isUndefined } from '@pepperhq/types-sdk';
import { v4 as uuid } from 'uuid';
import { partialResetLocalBasket, resetLocalBasket } from 'components/basket/localStore';
import { mapServerItemsToLocalBasket } from 'components/basket/helpers';
import type { AppDispatch } from 'store/store';

export interface BasketState {
    locationId: string;
    items: (IBasketItem & { price: number })[];
    adjustments: IBasketAdjustment[];
    deliveryLocation?: string;
    prepTimePadding?: number;
    checkId?: string;
    timeSlot?: Timeslot;
    notes: Record<string, string | undefined>;
    suggestionShowed: boolean;
    deliveryAddressId?: string;
    deliveryTimeMins?: number;
    hydrated?: boolean;
}

export const initialState: BasketState = {
    locationId: '',
    items: [],
    adjustments: [],
    checkId: undefined,
    deliveryLocation: undefined,
    notes: {},
    suggestionShowed: false,
    hydrated: false
};

type BasketItemWithModifiers = IBasketItem & { nestedModifierProducts?: IBasketItem[] };

const sortByProductId = (a: { productId: string }, b: { productId: string }) =>
    a.productId.localeCompare(b.productId);

function doBasketItemsMatch(existing: BasketItemWithModifiers, incoming: BasketItemWithModifiers): boolean {
    const productIdMatch = existing.productId === incoming.productId;
    const categoryIdMatch = existing.categoryId === incoming.categoryId;

    let modifierOptionsMatch = true;
    if (Array.isArray(existing.modifiers) && existing.modifiers.length > 0) {
        if (Array.isArray(incoming.modifiers) && incoming.modifiers.length > 0) {
            modifierOptionsMatch = isEqual([...existing.modifiers].sort(), [...incoming.modifiers].sort());
        } else {
            modifierOptionsMatch = false;
        }
    } else {
        modifierOptionsMatch = !Array.isArray(incoming.modifiers) || incoming.modifiers.length === 0;
    }

    const existingModifiers = existing.nestedModifierProducts?.map(item => ({
        ...item,
        parentReferenceId: undefined,
        referenceId: undefined,
        quantity: existing.quantity > 0 ? item.quantity / existing.quantity : 0
    }));
    const incomingModifiers = incoming.nestedModifierProducts?.map(item => ({
        ...item,
        parentReferenceId: undefined,
        referenceId: undefined,
        quantity: incoming.quantity > 0 ? item.quantity / incoming.quantity : 0
    }));

    const hasExistingModifiers = isDefined(existingModifiers) && existingModifiers.length > 0;
    const hasIncomingModifiers = isDefined(incomingModifiers) && incomingModifiers.length > 0;

    let modifierProductsMatch: boolean;
    if (hasExistingModifiers && hasIncomingModifiers) {
        modifierProductsMatch = isEqual(
            [...existingModifiers].sort(sortByProductId),
            [...incomingModifiers].sort(sortByProductId)
        );
    } else {
        // If one has modifier products and the other doesn't, they don't match
        modifierProductsMatch = hasExistingModifiers === hasIncomingModifiers;
    }

    return productIdMatch && categoryIdMatch && modifierOptionsMatch && modifierProductsMatch;
}

function findMatchingItemIndex(
    currentBasketItems: BasketItemWithModifiers[],
    itemToAdd: BasketItemWithModifiers,
    stateItems: IBasketItem[]
): number {
    for (const existing of currentBasketItems) {
        if (doBasketItemsMatch(existing, itemToAdd)) {
            return stateItems.findIndex(item => item.referenceId === existing.referenceId);
        }
    }
    return -1;
}

const basketSlice = createSlice({
    name: 'basket',
    initialState,
    reducers: {
        addItem(state, action: PayloadAction<IBasketItem & { price: number }>) {
            const item = action.payload;
            const matchIndex = state.items.findIndex(existing => {
                const productIdMatch = existing.productId === item.productId;
                let modifiersMatch = true;
                if (isDefined(existing.modifiers)) {
                    if (isDefined(item.modifiers)) {
                        modifiersMatch = isEqual([...existing.modifiers].sort(), [...item.modifiers].sort());
                    } else {
                        modifiersMatch = false;
                    }
                } else {
                    modifiersMatch = !isDefined(item.modifiers);
                }
                return productIdMatch && modifiersMatch;
            });
            if (matchIndex !== -1) {
                state.items[matchIndex].quantity += item.quantity;
            } else {
                state.items.push(item);
            }
        },
        addItems(state, action: PayloadAction<(IBasketItem & { price: number })[]>) {
            const itemsToAdd = action.payload;
            const plainItems = current(state.items);
            const currentBasketItems = plainItems
                .filter(item => isUndefined(item.parentReferenceId))
                .map(item => ({
                    ...item,
                    nestedModifierProducts: getAllModifierProductsForRootReferenceId(
                        plainItems,
                        item.referenceId ?? ''
                    )
                }));
            let itemToAdd: (IBasketItem & { nestedModifierProducts?: IBasketItem[] }) | null | undefined =
                itemsToAdd.find(item => isUndefined(item.parentReferenceId));
            itemToAdd = itemToAdd
                ? {
                      ...itemToAdd,
                      nestedModifierProducts: getAllModifierProductsForRootReferenceId(
                          itemsToAdd,
                          itemToAdd.referenceId ?? ''
                      )
                  }
                : null;
            if (!itemToAdd) {
                return;
            }
            const matchIndex = findMatchingItemIndex(currentBasketItems, itemToAdd, state.items);
            if (matchIndex !== -1) {
                state.items[matchIndex].quantity += itemToAdd.quantity;
                const mainReferenceId = state.items[matchIndex].referenceId ?? '';
                // update the quantity of the nested modifier products
                const nestedModifierProducts = getAllModifierProductsForRootReferenceId(
                    current(state.items),
                    mainReferenceId
                );
                for (const nestedModifierProduct of nestedModifierProducts) {
                    const nestedModifierProductIndex = state.items.findIndex(
                        item => item.referenceId === nestedModifierProduct.referenceId
                    );
                    if (nestedModifierProductIndex !== -1) {
                        const matchingModProd = itemToAdd.nestedModifierProducts?.find(
                            item =>
                                item.productId === nestedModifierProduct.productId &&
                                item.categoryId === nestedModifierProduct.categoryId
                        );
                        if (matchingModProd) {
                            state.items[nestedModifierProductIndex].quantity += matchingModProd.quantity;
                        }
                    }
                }
            } else {
                state.items.push(...itemsToAdd);
            }
        },
        updateQuantity(state, action: PayloadAction<{ itemReferenceId: string; quantity: number }>) {
            const { itemReferenceId, quantity } = action.payload;
            const itemIndex = state.items.findIndex(item => item.referenceId === itemReferenceId);
            if (itemIndex === -1) {
                return;
            }
            const originQuantity = state.items[itemIndex].quantity;
            state.items[itemIndex].quantity = quantity;
            const { referenceId } = state.items[itemIndex];
            if (referenceId) {
                const modifierProducts = getAllModifierProductsForRootReferenceId(state.items, referenceId);
                for (const modifierProduct of modifierProducts) {
                    modifierProduct.quantity = quantity * (modifierProduct.quantity / originQuantity);
                }
            }
            state.items = state.items.filter(item => item.quantity !== 0);
        },
        removeItem(state, action: PayloadAction<IProduct | IEnrichedProduct>) {
            const itemToRemove = action.payload;
            const itemIndex = state.items.findIndex(item => item.productId === itemToRemove.id);
            if (itemIndex === -1) {
                return;
            }
            const found = state.items[itemIndex];
            state.items = state.items.filter((item, index) => {
                const refIdChild =
                    isDefined(found.referenceId) &&
                    isDefined(item.parentReferenceId) &&
                    found.referenceId === item.parentReferenceId;
                return !refIdChild && index !== itemIndex;
            });
        },
        setBasket(state, action: PayloadAction<Partial<BasketState>>) {
            Object.assign(state, action.payload);
        },
        setBasketLocationId(state, action: PayloadAction<string | undefined>) {
            if (isDefined(action.payload)) {
                state.locationId = action.payload;
            }
        },
        resetBasketState(state) {
            return {
                ...initialState,
                deliveryLocation: state.deliveryLocation,
                checkId: state.checkId
            };
        },
        addAwardAdjustment(state, action: PayloadAction<IAdjustmentReadResourceV22>) {
            state.adjustments.push({
                awardId: action.payload.awardId,
                quantity: action.payload.quantity
            });
        },
        removeReward(state, action: PayloadAction<string>) {
            state.adjustments = state.adjustments.filter(adj => adj.awardId !== action.payload);
        },
        resetRewards(state) {
            state.adjustments = [];
        },
        setDeliveryLocation(state, action: PayloadAction<string | undefined>) {
            state.deliveryLocation = action.payload;
            state.checkId = undefined;
        },
        setCheckId(state, action: PayloadAction<string | undefined>) {
            state.checkId = action.payload;
            state.deliveryLocation = undefined;
        },
        setTimeslot(state, action: PayloadAction<Timeslot | undefined>) {
            if (action.payload) {
                state.timeSlot = action.payload as any;
            } else {
                delete state.timeSlot;
            }
        },
        setNote(state, action: PayloadAction<{ title: string; note?: string }>) {
            state.notes[action.payload.title] = action.payload.note;
        },
        setPrepTimePadding(state, action: PayloadAction<number | undefined>) {
            state.prepTimePadding = action.payload;
        },
        setSuggestionsShowed(state) {
            state.suggestionShowed = true;
        },
        setDeliveryTime(state, action: PayloadAction<number | undefined>) {
            state.deliveryTimeMins = action.payload;
        },
        setDeliveryAddressId(state, action: PayloadAction<string | undefined>) {
            state.deliveryAddressId = action.payload;
        },
        rehydrate(state, action: PayloadAction<Partial<BasketState> | undefined>) {
            if (action.payload) {
                Object.assign(state, action.payload);
            }
            state.hydrated = true;
        }
    }
});

export const {
    addItem,
    addItems,
    updateQuantity,
    removeItem,
    setBasket,
    setBasketLocationId,
    resetBasketState,
    addAwardAdjustment,
    removeReward,
    resetRewards,
    setDeliveryLocation,
    setCheckId,
    setTimeslot,
    setNote,
    setPrepTimePadding,
    setSuggestionsShowed,
    setDeliveryTime,
    setDeliveryAddressId,
    rehydrate
} = basketSlice.actions;

// Compound action creators (dispatch multiple actions)

export const addBasketItem =
    (
        item: IEnrichedProduct,
        categoryId: string,
        quantity: number,
        modifiers: IBasketItem['modifiers'],
        pricePerItem: number,
        referenceId?: string
    ) =>
    (dispatch: AppDispatch) => {
        const basketItem: IBasketItem & { price: number } = {
            categoryId,
            quantity,
            modifiers,
            referenceId: referenceId ?? uuid(),
            productId: item.id,
            price: pricePerItem
        };
        dispatch(resetRewards());
        dispatch(addItem(basketItem));
    };

export const addBasketItems = (items: IBasketItem[]) => (dispatch: AppDispatch) => {
    dispatch(resetRewards());
    dispatch(addItems(items as any));
};

export const removeBasketItem = (itemToRemove: IProduct | IEnrichedProduct) => (dispatch: AppDispatch) => {
    dispatch(resetRewards());
    dispatch(removeItem(itemToRemove));
};

export const updateBasketItemQuantity =
    (itemReferenceId: string | undefined, quantity: number) => (dispatch: AppDispatch) => {
        if (isUndefined(itemReferenceId)) {
            return;
        }
        dispatch(resetRewards());
        dispatch(updateQuantity({ itemReferenceId, quantity }));
    };

export const updateTimePadding = () => (dispatch: AppDispatch, getState: () => any) => {
    const { menu, basket } = getState();
    const { items, prepTimePadding } = basket;
    let newPadding = prepTimePadding;
    const productById = menu.menu?.productIdToProduct;
    if (isDefined(newPadding) && !!productById) {
        newPadding = 0;
        items.forEach((item: any) => {
            const product = productById.get(item.productId);
            if (
                isDefined(product) &&
                isNumber(product.prepTimeMins) &&
                product.prepTimeMins > Number(newPadding)
            ) {
                newPadding = product.prepTimeMins;
            }
        });
        if (newPadding > 0) {
            dispatch(setPrepTimePadding(newPadding));
            return;
        }
    }
    dispatch(setPrepTimePadding(undefined));
};

// resetBasket: dispatches resetBasketState + calls resetLocalBasket
export const resetBasket = () => (dispatch: AppDispatch) => {
    dispatch(resetBasketState());
    resetLocalBasket();
};

// partialResetBasket: dispatches resetBasketState + calls partialResetLocalBasket
export const partialResetBasket = () => (dispatch: AppDispatch) => {
    dispatch(resetBasketState());
    partialResetLocalBasket();
};

// syncBasketFromServer: replaces local basket items with server-returned items
export const syncBasketFromServer = (serverItems: IItemReadResourceV10[]) => (dispatch: AppDispatch) => {
    dispatch(setBasket({ items: mapServerItemsToLocalBasket(serverItems) }));
};

export default basketSlice.reducer;
